import React, { useCallback, useEffect, useState, useContext } from 'react';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import { Col, Row, Modal, OverlayTrigger, Tooltip, Badge } from 'react-bootstrap';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import Select from 'components/inputs/Select';
import TextInput from 'components/inputs/Input';
import SearchBox from 'components/general/SearchBox';
import { FiEdit2, FiEye, FiArchive } from 'react-icons/fi';
import * as _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import ReactPagination from 'components/general/ReactPagination';
import Footer from 'components/general/Footer';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import { UPDATE_STATUS_CODE, DELETE_STATUS_CODE, MANAGE_SESSION_LEVEL_OFFERS } from 'components/common/constant';
import { FETCH_SESSION_LEVEL_OFFERS, DELETE_SESSION_LEVEL_OFFER } from 'actions/sessionLevelOffer';

const LIMIT = 10;
export const STATUS_OPTIONS = ['Active', 'Scheduled', 'Expired', 'Archived'].map((s) => ({ label: s, value: s }));
export const getOfferId = (item) => _.get(item, 'id', _.get(item, '_id', ''));
export const formatOfferDate = (d) => (d ? moment(d).format('DD MMM YY, hh:mm A') : '-');
export const mutedText = { fontSize: 12, opacity: 0.6 };
const STATUS_BADGE_BG = { Active: 'success', Scheduled: 'primary', Expired: 'secondary', Archived: 'danger' };

export const renderOfferStatus = (status, archivesAt) => (
  <React.Fragment>
    <Badge pill bg={STATUS_BADGE_BG[status]}>
      {status}
    </Badge>
    {archivesAt && status !== 'Archived' && <div style={mutedText}>archives {formatOfferDate(archivesAt)}</div>}
  </React.Fragment>
);

export const archiveEndError = (start, end) => (start && end && !moment(end).isAfter(moment(start)) ? 'End date must be after the start date' : null);

export const getSessionLevelOfferStatus = (item) => {
  const now = moment();
  const ended = _.get(item, 'end_date') && moment(item.end_date).isBefore(now);
  if (_.get(item, 'deleted') || (ended && _.get(item, 'archived_at'))) return 'Archived';
  if (ended) return 'Expired';
  if (moment(_.get(item, 'start_date')).isAfter(now)) return 'Scheduled';
  return 'Active';
};

const SessionLevelOfferList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { navigateTo } = useContext(NavContext);
  const [archiveId, setArchiveId] = useState('');
  const [archiveStart, setArchiveStart] = useState('');
  const [archiveEnd, setArchiveEnd] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);

  const offers = useSelector((state) => state.sessionLevelOffer.sessionLevelOffers);
  const isLoader = useSelector((state) => state.sessionLevelOffer.isLoading);
  const rolePermissionData = useSelector((state) => state.manageRole.manageRoles);
  const profileData = useSelector((state) => state.profile.userProfile);
  const currentUserName = _.get(profileData, 'name', '');
  const canManage = _.some(_.get(rolePermissionData, '[0].permissions', []), { name: MANAGE_SESSION_LEVEL_OFFERS });

  const getOffers = useCallback(() => {
    dispatch({ type: FETCH_SESSION_LEVEL_OFFERS.REQUEST });
  }, []);

  useEffect(() => {
    getOffers();
  }, []);

  const archiveOffer = useCallback(() => {
    dispatch({
      type: DELETE_SESSION_LEVEL_OFFER.REQUEST,
      payload: { id: archiveId, start_date: moment(archiveStart).toISOString(), end_date: archiveEnd ? moment(archiveEnd).toISOString() : null },
      cb: (res) => {
        if (_.get(res, 'status') === DELETE_STATUS_CODE || _.get(res, 'status') === UPDATE_STATUS_CODE) {
          setArchiveId('');
          getOffers();
        }
      },
    });
  }, [archiveId, archiveStart, archiveEnd]);

  const search = _.toLower(_.trim(searchText));
  const filtered = _.filter(
    offers,
    (item) =>
      (!statusFilter || getSessionLevelOfferStatus(item) === statusFilter) && (!search || _.includes(_.toLower(_.get(item, 'title', '')), search))
  );
  const rows = filtered.slice((page - 1) * LIMIT, page * LIMIT);
  const endError = archiveEndError(archiveStart, archiveEnd);

  return (
    <React.Fragment>
      <LayoutContainer>
        <AdminHeader title={t('header.sessionLevelOffers', 'Session Level Offers')} />
        <SkeletonTheme baseColor="#1b2a38" highlightColor="#15212b" enableAnimation="true">
          <div className="page-content-wrapper scrollable">
            <div className="user-group-page_main">
              <div className="user-group-main-block">
                <Card>
                  <div className="user-group-block">
                    <div className="user-group-search-box">
                      <Row className="align-items-center">
                        <Col xl={4}>
                          <SearchBox
                            placeholder="Search by title"
                            value={searchText}
                            onChange={(e) => {
                              setSearchText(e.target.value);
                              setPage(1);
                            }}
                          />
                        </Col>
                        <Col xl={3}>
                          <Select
                            placeholder="All statuses"
                            options={STATUS_OPTIONS}
                            disableSort
                            value={statusFilter}
                            isClearable
                            onChange={(val) => {
                              setStatusFilter(val || '');
                              setPage(1);
                            }}
                          />
                        </Col>
                        <Col xl={5} className="d-flex justify-content-end align-items-center" style={{ gap: 16 }}>
                          {!isLoader && (
                            <span style={mutedText}>
                              {filtered.length} {filtered.length === 1 ? 'offer' : 'offers'}
                            </span>
                          )}
                          {canManage && (
                            <Button className="user-group-btn" onClick={() => navigateTo('/addSessionLevelOffer')}>
                              {t('button.addOffer', 'Add Offer')}
                            </Button>
                          )}
                        </Col>
                      </Row>
                    </div>
                    <div className="user-group-table">
                      <div className="table-responsive">
                        <table className="record-list-table">
                          <thead>
                            <tr>
                              <th className="px-3">Title</th>
                              <th className="px-3">Stations</th>
                              <th className="px-3">Type</th>
                              <th className="px-3">Start</th>
                              <th className="px-3">End</th>
                              <th className="px-3">Status</th>
                              <th className="px-3 action-col">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {isLoader ? (
                              <tr>
                                <td colSpan={7} className="border-0">
                                  <Skeleton height={15} />
                                </td>
                              </tr>
                            ) : _.isEmpty(rows) ? (
                              <tr>
                                <td colSpan={7} className="border-0">
                                  <div className="empty-data-block">
                                    <div className="mb-3">No offers found</div>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              _.map(rows, (item, index) => {
                                const id = getOfferId(item);
                                const status = getSessionLevelOfferStatus(item);
                                const stations = _.get(item, 'stations', []);
                                const title = _.get(item, 'title', '');
                                return (
                                  <tr key={`session-level-offer-${index}`}>
                                    <td className="px-3">
                                      <OverlayTrigger placement="top" overlay={<Tooltip id={`title-${index}`}>{title}</Tooltip>}>
                                        <div style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
                                      </OverlayTrigger>
                                    </td>
                                    <td className="px-3">
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id={`stations-${index}`}>{_.map(stations, 'name').join(', ') || '-'}</Tooltip>}
                                      >
                                        <span>
                                          {stations.length} {stations.length === 1 ? 'station' : 'stations'}
                                        </span>
                                      </OverlayTrigger>
                                    </td>
                                    <td className="px-3">
                                      {_.isEmpty(_.get(item, 'session_cashback_distribution')) ? 'Price range' : 'Session sequence'}
                                    </td>
                                    <td className="px-3">{formatOfferDate(_.get(item, 'start_date'))}</td>
                                    <td className="px-3">{formatOfferDate(_.get(item, 'end_date'))}</td>
                                    <td className="px-3">{renderOfferStatus(status, _.get(item, 'archived_at') && _.get(item, 'end_date'))}</td>
                                    <td className="px-3">
                                      <span
                                        className="user-group-table-editIcon"
                                        title={canManage ? 'Edit' : 'View'}
                                        onClick={() => navigateTo(`/editSessionLevelOffer/${id}`)}
                                      >
                                        {canManage ? <FiEdit2 /> : <FiEye />}
                                      </span>
                                      {canManage && (
                                        <span
                                          className="user-group-table-deleteIcon"
                                          title="Archive"
                                          onClick={() => {
                                            setArchiveId(id);
                                            setArchiveStart('');
                                            setArchiveEnd('');
                                          }}
                                        >
                                          <FiArchive />
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {!isLoader && filtered.length > LIMIT && (
                      <ReactPagination
                        currentPage={page}
                        limit={LIMIT}
                        total={filtered.length}
                        handlePageClick={(pageVal) => setPage(pageVal.selected + 1)}
                        totalPages={Math.ceil(filtered.length / LIMIT)}
                        marginPagesDisplayed={1}
                      />
                    )}
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </SkeletonTheme>
        {archiveId && (
          <Modal show centered size="lg" onHide={() => setArchiveId('')}>
            <div className="delete-modal-main_page">
              <div className="delete-modal--inner">
                <div className="delete-modal---block_title">
                  <div className="inner--block_title">
                    <div className="title">Archive Offer</div>
                    <p className="sub-title">The offer keeps running until the end date, then shows as Archived. The record is kept.</p>
                    <Row>
                      <Col md={6}>
                        <TextInput
                          isRequired
                          type="datetime-local"
                          label="Start Date"
                          value={archiveStart}
                          onChange={(e) => setArchiveStart(e.target.value)}
                        />
                      </Col>
                      <Col md={6}>
                        <TextInput
                          type="datetime-local"
                          label="End Date (Optional, blank = no end)"
                          value={archiveEnd}
                          min={archiveStart || undefined}
                          onChange={(e) => setArchiveEnd(e.target.value)}
                          error={endError}
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
                <div className="delete-modal-input-box__block">
                  <Row>
                    <Col lg={6}>
                      <Button className="delete-model--btn cancel--btn" onClick={() => setArchiveId('')}>
                        {t('button.cancel', 'Cancel')}
                      </Button>
                    </Col>
                    <Col lg={6}>
                      <Button className="delete-model--btn remove--btn" onClick={archiveOffer} disabled={!archiveStart || !!endError || isLoader}>
                        Archive
                      </Button>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          </Modal>
        )}
        {currentUserName === 'CZ Tenant Admin' ? null : <Footer />}
      </LayoutContainer>
    </React.Fragment>
  );
};

export default SessionLevelOfferList;
